import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  Button,
  FormRow,
  IconButton,
  Input,
  Modal,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFieldArray, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { useNotification } from '../shared/notification/useNotification';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { Partner } from '../api/govos.types';

interface MarketplaceAccessKeysFormData {
  keys: { value: string }[];
}

interface EditMarketplaceAccessKeysModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  zustaendigeStelle: Partner;
}

export const parseAccessKeys = (value?: string): string[] =>
  (value ?? '')
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);

const getDefaultValues = (data?: Partner): MarketplaceAccessKeysFormData => ({
  keys: parseAccessKeys(data?.marketplaceAccessKeys).map((value) => ({ value }))
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>,
  t: TFunction<'translation', undefined>
) =>
  CustomJoi.object<MarketplaceAccessKeysFormData>({
    keys: CustomJoi.array().items(
      CustomJoi.object({
        value: CustomJoi.string()
          .trim()
          .required()
          .messages({
            'string.empty': t(
              'EditMarketplaceAccessKeysModal.validation.emptyKey'
            ),
            'any.required': t(
              'EditMarketplaceAccessKeysModal.validation.emptyKey'
            )
          })
      })
    )
  });

export function EditMarketplaceAccessKeysModal({
  onClose,
  onSuccess,
  isOpen,
  zustaendigeStelle
}: EditMarketplaceAccessKeysModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const CustomJoi = createJoiWithTranslations(t);

  const { register, handleSubmit, formState, control } =
    useForm<MarketplaceAccessKeysFormData>({
      defaultValues: getDefaultValues(zustaendigeStelle),
      resolver: joiResolver(getValidationSchema(CustomJoi, t)),
      mode: 'onChange'
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keys'
  });

  const { mutate: saveAccessKeys, isPending } = useMutation({
    mutationFn: async (data: MarketplaceAccessKeysFormData) => {
      const marketplaceAccessKeys = data.keys
        .map((key) => key.value.trim())
        .filter((key) => key.length > 0)
        .join(',');

      return notifyUserFromRequest(
        () =>
          govosClient.put(
            GovosApiRoutes.zustaendigeStelle(zustaendigeStelle?.appiid),
            { 'zs.marketplaceAccessKeys': marketplaceAccessKeys }
          ),
        {
          successMessage: t('EditMarketplaceAccessKeysModal.successMessage'),
          errorMessage: t('EditMarketplaceAccessKeysModal.errorMessage'),
          loadingMessage: t('EditMarketplaceAccessKeysModal.loadingMessage')
        }
      );
    },
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [GovosApiRoutes.zustaendigeStelle(zustaendigeStelle.appiid)]
      });
      onSuccess?.();
    }
  });

  const handleSave = handleSubmit((data) => {
    saveAccessKeys(data);
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="m" strong>
          {t('EditMarketplaceAccessKeysModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l" orientation="vertical">
          <Text>
            {t('EditMarketplaceAccessKeysModal.zustaendigeStelle')}{' '}
            <strong>{zustaendigeStelle.dispname}</strong>
          </Text>

          <Stack spacing="m" orientation="vertical">
            {fields.map((field, index) => (
              <FormRow
                key={field.id}
                label={t('EditMarketplaceAccessKeysModal.keyLabel', {
                  number: index + 1
                })}
                validationMessage={
                  formState.errors.keys?.[index]?.value?.message
                }
                variant={
                  formState.errors.keys?.[index]?.value ? 'error' : undefined
                }
              >
                <Stack spacing="xs" direction="row" alignItems="center">
                  <Stack style={{ flex: 1 }}>
                    <Input {...register(`keys.${index}.value`)} />
                  </Stack>
                  <IconButton
                    variant="primary-text"
                    icon="trashcan"
                    label={t('EditMarketplaceAccessKeysModal.actions.remove')}
                    onClick={() => remove(index)}
                  />
                </Stack>
              </FormRow>
            ))}

            <Stack direction="row">
              <Button
                size={'sm'}
                variant="primary-text"
                beforeIcon="add"
                onClick={() => append({ value: '' })}
                style={{ paddingLeft: 0 }}
              >
                {t('EditMarketplaceAccessKeysModal.actions.addKey')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack spacing="xs" orientation="vertical" style={{ width: '100%' }}>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={isPending || Object.keys(formState.errors).length > 0}
            style={{ width: '100%' }}
          >
            {t('EditMarketplaceAccessKeysModal.actions.save')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            disabled={isPending}
            style={{ width: '100%' }}
          >
            {t('EditMarketplaceAccessKeysModal.actions.cancel')}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
